import { Endpoint, Resource } from '@rabbiagency/rabbi-resources';
import { apiRequest, apiResponse, apiResponseBody } from 'common/resources/interceptors';
import UrlHelper from 'common/utils/url-helper';
import { PARAMS } from 'common/constants';
import { attachJwtToken } from './interceptors';

const { STORY_ID, ORGANIZATION_ID, DOMAIN_ID, TEMPLATE_ID, TEAM_ID, MEMBER_ID } = PARAMS;

const backendResource = new Resource(UrlHelper.adminBackend, {
	headers: { Accept: 'application/json' },
	requestInterceptors: [apiRequest, attachJwtToken],
	responseInterceptors: [apiResponse, apiResponseBody],
});

const login = new Endpoint(backendResource, '/auth/login');
const verify = new Endpoint(backendResource, '/auth/me', { type: 'GET' });
const logout = new Endpoint(backendResource, '/logout');

// story:
const getStory = new Endpoint(backendResource, `/story/:${STORY_ID}`, { type: 'GET', storyAdapter: true });
const createStory = new Endpoint(backendResource, '/story');
const duplicateStory = new Endpoint(backendResource, `/story/:${STORY_ID}/duplicate`, { type: 'POST' });
const deleteStory = new Endpoint(backendResource, `/story/:${STORY_ID}`, { type: 'DELETE' });
const updateStory = new Endpoint(backendResource, `/story/:${STORY_ID}`, { type: 'PUT' });
const updateLatestStory = new Endpoint(backendResource, `/story/:${STORY_ID}/latest`, { type: 'PUT' });
const updateStoryUrl = new Endpoint(backendResource, `/story/:${STORY_ID}/client-story-id`, { type: 'PATCH' });
const updatePublishedStory = new Endpoint(backendResource, `/story/:${STORY_ID}/published`, { type: 'PUT' });
const unpublishPublishedStory = new Endpoint(backendResource, `/story/:${STORY_ID}/unpublish`, { type: 'POST' });
const postGallery = new Endpoint(backendResource, `/story/:${STORY_ID}/gallery`, { type: 'POST' });
const getStoryAudience = new Endpoint(backendResource, `/story/:${STORY_ID}/audience`, {
	type: 'POST',
});
const getReportStoryBigPicture = new Endpoint(backendResource, `/story/:${STORY_ID}/analytics/big-picture`, {
	type: 'GET',
});
const getReportStoryByCard = new Endpoint(backendResource, `/story/:${STORY_ID}/analytics/card-by-card`, {
	type: 'GET',
});
const getCsvUrlStoryByCard = new Endpoint(backendResource, `/story/:${STORY_ID}/analytics/card-by-card`, {
	type: 'POST',
});
const storyRollback = new Endpoint(backendResource, `/story/:${STORY_ID}/rollback`, { type: 'POST' });

const setDefaultStoryDomain = new Endpoint(backendResource, `/story/:${STORY_ID}/domain`, {
	type: 'PATCH',
});

// member:
const updateMemberEmail = new Endpoint(backendResource, `/member/:${MEMBER_ID}/email`, { type: 'PATCH' });
const getMember = new Endpoint(backendResource, `/member/:${MEMBER_ID}`, { type: 'GET' });
const resetPassword = new Endpoint(backendResource, `/member/password/reset`, { type: 'POST' });
const restorePassword = new Endpoint(backendResource, `/member/password/restore`, { type: 'POST' });
const getMemberPreferences = new Endpoint(backendResource, `/member/:${MEMBER_ID}/preferences`, { type: 'GET' });
const setMemberPreferences = new Endpoint(backendResource, `/member/:${MEMBER_ID}/preferences`, { type: 'POST' });
const updateMemberAvatar = new Endpoint(backendResource, `/member/:${MEMBER_ID}/profile-picture`, { type: 'PATCH' });
const updateMemberPassword = new Endpoint(backendResource, `/member/:${MEMBER_ID}/password`, { type: 'PATCH' });
const updateMember = new Endpoint(backendResource, `/member/:${MEMBER_ID}`, { type: 'PUT' });
const verifyMember = new Endpoint(backendResource, `/member/verify`, { type: 'POST' });
const verifyMemberResend = new Endpoint(backendResource, `/member/:${MEMBER_ID}/email/verify`, { type: 'POST' });
const createMember = new Endpoint(backendResource, `/member`, { type: 'POST' });
const deleteMember = new Endpoint(backendResource, `/member/:${MEMBER_ID}`, { type: 'DELETE' });

// team:
const createTeam = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/team`, { type: 'POST' });
const getTeam = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/team/:${TEAM_ID}`, { type: 'GET' });
const updateTeam = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/team/:${TEAM_ID}`, { type: 'PUT' });
const deleteTeam = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/team/:${TEAM_ID}`, {
	type: 'DELETE',
});
const getTeamMembers = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/team/:${TEAM_ID}/member`, {
	type: 'GET',
});
const updateTeamMember = new Endpoint(
	backendResource,
	`/organization/:${ORGANIZATION_ID}/team/:${TEAM_ID}/member/:${MEMBER_ID}`,
	{ type: 'PUT' }
);
const deleteTeamMember = new Endpoint(
	backendResource,
	`/organization/:${ORGANIZATION_ID}/team/:${TEAM_ID}/member/:${MEMBER_ID}`,
	{ type: 'DELETE' }
);

// payment plans
const getPlans = new Endpoint(backendResource, `/plan`, { type: 'GET' });
const getPlanChargeInfo = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/plan-switch`, {
	type: 'GET',
});

// organization:
const createOrganization = new Endpoint(backendResource, '/organization', { type: 'POST' });
const validateOrganizationName = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}`, { type: 'HEAD' });
const updateOrganizationSettings = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}`, { type: 'PUT' });
const getOrganizationTeams = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/team`, { type: 'GET' });
const getPubNubAuthKey = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/pub-sub-key`, {
	type: 'GET',
});
const addMembersToOrganizationTeams = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/invite`, {
	type: 'POST',
});
const getOrganizationMembers = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/member`, {
	type: 'GET',
});
const getOrganizationGoogleFonts = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/fonts?sort=alpha`, {
	type: 'GET',
});
const getOrganizationAdminInvoices = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/invoice`, {
	type: 'GET',
});
const getOrganizationAdminBilling = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/billing`, {
	type: 'GET',
});
const getOrganizationDomains = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/domain`, {
	type: 'GET',
});
const removeMemberFromOrganization = new Endpoint(
	backendResource,
	`/organization/:${ORGANIZATION_ID}/member/:${MEMBER_ID}`,
	{ type: 'DELETE' }
);
const removeOrganizationDomain = new Endpoint(
	backendResource,
	`/organization/:${ORGANIZATION_ID}/domain/:${DOMAIN_ID}`,
	{ type: 'DELETE' }
);
const retryOrganizationDomainConnection = new Endpoint(
	backendResource,
	`/organization/:${ORGANIZATION_ID}/domain/:${DOMAIN_ID}/retry`,
	{ type: 'POST' }
);
const updateOrganizationMember = new Endpoint(
	backendResource,
	`/organization/:${ORGANIZATION_ID}/member/:${MEMBER_ID}`,
	{ type: 'PUT' }
);
const updateOrganizationBilling = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/billing`, {
	type: 'PUT',
});
const updateOrganizationPayment = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/payment`, {
	type: 'PUT',
});
const resendInviteToOrganization = new Endpoint(
	backendResource,
	`/organization/:${ORGANIZATION_ID}/member/:${MEMBER_ID}/invite-resend`,
	{ type: 'POST' }
);
const updateOrganizationDomain = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/domain`, {
	type: 'POST',
});
const setDefaultOrganizationDomain = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/domain`, {
	type: 'PATCH',
});
const getOrganizationDomainStatus = new Endpoint(
	backendResource,
	`/organization/:${ORGANIZATION_ID}/domain/:${DOMAIN_ID}`,
	{
		type: 'GET',
	}
);
const getOrganizationStories = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/story`, {
	type: 'GET',
});
const getOrganizationTeamsOfMember = new Endpoint(
	backendResource,
	`/organization/:${ORGANIZATION_ID}/member/:${MEMBER_ID}`,
	{ type: 'GET' }
);

const acceptInviteToOrganization = new Endpoint(
	backendResource,
	`/organization/:${ORGANIZATION_ID}/member/:${MEMBER_ID}/invite-accept`,
	{ type: 'POST' }
);

const setupInviteToOrganization = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/invite-setup`, {
	type: 'POST',
});
const cancelSubscription = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/cancel-subscription`, {
	type: 'POST',
});
const upgradeSubscription = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/plan`, {
	type: 'PUT',
});
const postOrgGallery = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/asset`, { type: 'POST' });

// templates
const getTemplates = new Endpoint(backendResource, '/template', {
	type: 'GET',
});

const getTemplate = new Endpoint(backendResource, `/template/:${TEMPLATE_ID}`, {
	type: 'GET',
});

// analytics:
const getReportStories = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/reports-page`, {
	type: 'GET',
});

// oauth:
const OAuthVerify = new Endpoint(backendResource, '/oauth/login', { type: 'GET' });
const OAuthLogin = new Endpoint(backendResource, '/oauth/login', { type: 'POST' });
const OAuthConsent = new Endpoint(backendResource, '/oauth/consent', { type: 'GET' });
const OAuthConsentUpdate = new Endpoint(backendResource, '/oauth/consent', { type: 'POST' });

export {
	login,
	verify,
	logout,
	// story:
	getStory,
	createStory,
	duplicateStory,
	deleteStory,
	updateStory,
	updateLatestStory,
	updatePublishedStory,
	unpublishPublishedStory,
	updateStoryUrl,
	postGallery,
	getReportStoryBigPicture,
	storyRollback,
	getReportStoryByCard,
	getCsvUrlStoryByCard,
	setDefaultStoryDomain,
	// member:
	getMember,
	resetPassword,
	restorePassword,
	getMemberPreferences,
	setMemberPreferences,
	updateMember,
	updateMemberAvatar,
	updateMemberEmail,
	updateMemberPassword,
	deleteMember,
	createMember,
	verifyMember,
	verifyMemberResend,
	// team:
	createTeam,
	getTeam,
	updateTeam,
	deleteTeam,
	getTeamMembers,
	updateTeamMember,
	deleteTeamMember,
	// organization:
	createOrganization,
	getOrganizationAdminBilling,
	getOrganizationAdminInvoices,
	updateOrganizationBilling,
	updateOrganizationPayment,
	getPubNubAuthKey,
	getOrganizationGoogleFonts,
	getOrganizationTeamsOfMember,
	getOrganizationTeams,
	addMembersToOrganizationTeams,
	removeMemberFromOrganization,
	validateOrganizationName,
	getOrganizationMembers,
	updateOrganizationSettings,
	updateOrganizationMember,
	acceptInviteToOrganization,
	setupInviteToOrganization,
	resendInviteToOrganization,
	cancelSubscription,
	upgradeSubscription,
	postOrgGallery,
	updateOrganizationDomain,
	getOrganizationDomainStatus,
	getOrganizationDomains,
	getOrganizationStories,
	removeOrganizationDomain,
	retryOrganizationDomainConnection,
	setDefaultOrganizationDomain,
	// payment plans
	getPlans,
	getPlanChargeInfo,
	// templates
	getTemplates,
	getTemplate,
	// analytics:
	getReportStories,
	getStoryAudience,
	// auth:
	OAuthVerify,
	OAuthLogin,
	OAuthConsent,
	OAuthConsentUpdate,
};
